/**
 * Structured Logging System
 * Replaces console.log with environment-aware logging
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4,
}

interface LogContext {
  [key: string]: unknown
}

class Logger {
  private level: LogLevel
  private isDevelopment: boolean

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development'
    this.level = this.isDevelopment ? LogLevel.DEBUG : LogLevel.WARN
  }

  setLevel(level: LogLevel) {
    this.level = level
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.level
  }

  private formatMessage(level: string, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString()
    const contextStr = context ? ` | ${JSON.stringify(context)}` : ''
    return `[${timestamp}] [${level}] ${message}${contextStr}`
  }

  debug(message: string, context?: LogContext) {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.debug(this.formatMessage('DEBUG', message, context))
    }
  }

  info(message: string, context?: LogContext) {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(this.formatMessage('INFO', message, context))
    }
  }

  warn(message: string, context?: LogContext) {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(this.formatMessage('WARN', message, context))
    }
  }

  error(message: string, error?: Error | unknown, context?: LogContext) {
    if (this.shouldLog(LogLevel.ERROR)) {
      const errorContext = error instanceof Error 
        ? { ...context, error: error.message, stack: error.stack }
        : { ...context, error }
      
      console.error(this.formatMessage('ERROR', message, errorContext))
    }
  }

  // Store-specific loggers
  auth(message: string, context?: LogContext) {
    this.debug(`[AUTH] ${message}`, context)
  }

  devices(message: string, context?: LogContext) {
    this.debug(`[DEVICES] ${message}`, context)
  }

  customer(message: string, context?: LogContext) {
    this.debug(`[CUSTOMER] ${message}`, context)
  }

  reporting(message: string, context?: LogContext) {
    this.debug(`[REPORTING] ${message}`, context)
  }

  api(message: string, context?: LogContext) {
    this.debug(`[API] ${message}`, context)
  }
}

export const logger = new Logger()
